"use client"

import * as React from "react"
import { CalendarIcon } from "lucide-react"
import { cn } from "@workspace/ui/lib/utils"
import { Calendar } from "@workspace/ui/components/shadcn/calendar"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@workspace/ui/components/shadcn/popover"
import { Label } from "@workspace/ui/components/shadcn/label"
import type { FormApi, FieldDef } from "./fieldTypes"
import {
    normalizeString,
    getRequiredMark,
    getHelpText,
    getErrorText,
} from "./fieldUtils"

type DateDef = Extract<FieldDef, { type: "date" }>

type Props = {
    def: DateDef
    form: FormApi
    invalid: boolean
}

function isoToDate(v: string): Date | null {
    const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!m) return null
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

function dateToIso(d: Date): string {
    const y = String(d.getFullYear()).padStart(4, "0")
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const da = String(d.getDate()).padStart(2, "0")
    return `${y}-${m}-${da}`
}

function isoToDe(v: string): string {
    const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!m) return v
    return `${m[3]}.${m[2]}.${m[1]}`
}

function frameClass(invalid: boolean) {
    return cn(
        "ui-control",
        "h-9 w-full flex items-stretch cursor-pointer",
        invalid && "ui-control-error",
        !invalid && "ui-control-focus"
    )
}

export function FieldDate({ def, form, invalid }: Props) {
    const r = form.register(def.name)
    const raw = normalizeString(r.value)
    const date = React.useMemo(() => isoToDate(raw), [raw])
    const [open, setOpen] = React.useState(false)

    const requiredMark = getRequiredMark(def)
    const helpText = getHelpText(def)
    const err = (form.formState.errors as any)?.[def.name]
    const errorText = getErrorText(err)

    const validate = () => {
        r.onBlur()
        form.validateField?.(def.name)
        void form.validateFieldUnique?.(def.name)
    }

    return (
        <>
            <div className="ui-field-header">
                {def.label ? (
                    <Label htmlFor={def.name} className="ui-field-label">
                        {def.label}
                        {requiredMark}
                    </Label>
                ) : (
                    <span />
                )}
                {helpText ? (
                    <span className="ui-field-helptext">
                        {helpText}
                    </span>
                ) : (
                    <span />
                )}
            </div>

            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        className={frameClass(invalid)}
                        onClick={() => setOpen(true)}
                        onBlur={validate}
                    >
                        <div
                            className={cn(
                                "flex-1 px-3 flex items-center truncate ",
                                !date && "text-muted-foreground"
                            )}
                        >
                            {date ? isoToDe(raw) : ""}
                        </div>

                        <div className="flex items-center justify-center px-3 border-l border-border text-muted-foreground">
                            <CalendarIcon className="size-4 opacity-70" />
                        </div>
                    </button>
                </PopoverTrigger>

                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        mode="single"
                        selected={date ?? undefined}
                        captionLayout="dropdown"
                        weekStartsOn={1}
                        onSelect={(d) => {
                            if (!d) return
                            r.onChange(dateToIso(d))
                            validate()
                            setOpen(false)
                        }}
                        formatters={{
                            formatMonthDropdown: (m) =>
                                m.toLocaleString("de-DE", { month: "short" }),
                            formatCaption: (m) =>
                                m.toLocaleString("de-DE", {
                                    month: "long",
                                    year: "numeric",
                                }),
                            formatWeekdayName: (d) =>
                                d.toLocaleString("de-DE", { weekday: "short" }),
                        }}
                    />
                </PopoverContent>
            </Popover>

            <div className="ui-field-error">
                {errorText || null}
            </div>
        </>
    )
}
